import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Send, AlertTriangle, CheckCircle2, Clock, Info, FileText, Tag, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format, humanizeStatus } from "@/lib/brand-tokens";
import {
  reservations, getDealById, getBrandById, getCardsBySubmission, submissions,
  cancelReservation, submitCards,
} from "@/lib/mock-data";
import { useStoreVersion } from "@/hooks/useStore";
import { sampleCardLines } from "@/lib/card-samples";
import { toast } from "sonner";

// MEM-05 — Reservation detail + submit cards (the heart of the platform)
export default function ReservationDetailPage() {
  useStoreVersion();
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const reservation = reservations.find((r) => r.id === reservationId) || reservations[0];
  const deal = getDealById(reservation.dealId);
  // Brand is locked at reservation time (the new per-brand cap model means each
  // reservation is bound to exactly one brand). Fall back to the deal's primary
  // for legacy reservations that pre-date the brandId field.
  const chosenBrandId = reservation.brandId || deal?.brandId;
  const chosenBrand = getBrandById(chosenBrandId);
  // Brand-specific config (rate, perMemberCap, face range) is the source of
  // truth for THIS reservation — never use deal.rate (aggregate) here.
  const chosenCfg = (deal?.brandConfigs || []).find(c => c.brandId === chosenBrandId)
                 || (deal?.brandConfigs || [])[0];
  const isMultiBrand = !!deal?.isMultiBrand;
  const [raw, setRaw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const parsed = useMemo(() => parseRaw(raw), [raw]);
  const validCount = parsed.filter((r) => r.status === "valid").length;
  const totalFace = parsed.filter((r) => r.faceValue).reduce((acc, r) => acc + r.faceValue, 0);

  // Already submitted cards for this reservation.
  // NOTE: depend on submissions.length + cards.length so a new submit
  // triggers a re-derivation (the page itself is already subscribed via
  // useStoreVersion). Without this, the Pending chip + 'Cards already
  // submitted' table only refresh on a hard reload.
  const submittedCards = useMemo(() => {
    const subs = submissions.filter((s) => s.reservationId === reservation.id);
    return subs
      .flatMap((s) => getCardsBySubmission(s.id).map((c) => ({ ...c, submittedAt: s.submittedAt })))
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation.id, submissions.length, reservation.cardsSubmitted, reservation.cardsApproved]);

  const totalApprovedFace = submittedCards
    .filter((c) => c.status === "Approved")
    .reduce((a, c) => a + c.faceValue, 0);
  const totalPendingFace = submittedCards
    .filter((c) => c.status === "Pending")
    .reduce((a, c) => a + c.faceValue, 0);
  const fillPct = Math.min(100, Math.round((totalApprovedFace / reservation.faceValueReserved) * 100));

  const days = Math.max(0, Math.ceil((new Date(deal?.deadline) - Date.now()) / 86_400_000));

  // Cancel eligibility: member can cancel any reservation that hasn't been
  // fully closed and where the deal deadline hasn't passed. Cards already
  // submitted/approved against this reservation will still be paid; cancel
  // releases the unused face back to the brand pool.
  const dealExpired = new Date(deal?.deadline).getTime() < Date.now();
  const isTerminal = reservation.status === "Cancelled"
                  || reservation.status === "ExpiredReleased"
                  || reservation.status === "SubmittedFinal";
  const canCancel = !isTerminal && !dealExpired;
  const cancelDisabledReason = !canCancel
    ? (isTerminal
        ? `This reservation is already ${humanizeStatus(reservation.status).toLowerCase()}.`
        : "The deal deadline has passed — reservations can no longer be cancelled.")
    : null;
  const [cancelOpen, setCancelOpen] = useState(false);

  const handleCancel = () => {
    cancelReservation(reservation.id);
    setCancelOpen(false);
    toast.success("Reservation cancelled", {
      description: submittedCards.length > 0
        ? `Already-submitted cards will still be paid out. Unused ${format.currency(reservation.faceValueReserved - totalApprovedFace)} released back to ${chosenBrand?.name}.`
        : `${format.currency(reservation.faceValueReserved)} released back to ${chosenBrand?.name}.`,
    });
    setTimeout(() => navigate("/member/reservations"), 800);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    try {
      // Actually persist the submission + cards. Status starts as Pending;
      // admin approval flips them to Approved live via useStoreVersion().
      submitCards(reservation.id, parsed);
      toast.success(`Submitted ${validCount} ${chosenBrand?.name} card${validCount === 1 ? "" : "s"} · ${format.currency(totalFace)} face value`, {
        description: "Cards are queued for admin review. The status will update here as soon as they're approved.",
      });
      setRaw("");
    } catch (e) {
      toast.error("Submission failed", { description: e?.message || "Please review your inputs." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reservation-detail-page" className="space-y-6">
      <div>
        <Link to="/member/reservations" className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink">
          <ChevronLeft className="size-3.5" /> All reservations
        </Link>
      </div>

      {/* Header */}
      <div className="bg-surface border border-line rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <BrandStack brandIds={chosenBrand ? [chosenBrand.id] : deal?.brandIds} size="lg" />
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-brand text-h1 text-ink font-semibold">
                  {deal?.name}
                </h1>
                <StatusBadge entity="reservation" status={reservation.status} dot />
                {isMultiBrand && <span className="text-caption text-brand-700 font-semibold px-2 py-0.5 rounded bg-brand-50 border border-brand-100">Brand-locked</span>}
              </div>
              <div className="mt-1 text-caption text-ink-subtle">{chosenBrand?.name}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 text-right">
            <KV label="Reserved" value={format.currency(reservation.faceValueReserved)} />
            <KV label="Rate" value={`${chosenCfg?.rate ?? deal?.rate}%`} />
            <KV label="Expected payout" value={format.currency(reservation.expectedPayout)} accent />
            <KV label="Closes in" value={dealExpired ? "Expired" : `${days} day${days === 1 ? "" : "s"}`} />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-caption text-ink-subtle mb-1.5">
            <span>Cards approved</span>
            <span className="tabular-nums">
              {format.currency(totalApprovedFace)} of {format.currency(reservation.faceValueReserved)}
              {totalPendingFace > 0 && (
                <span className="ml-2 text-amber-700 font-medium">· {format.currency(totalPendingFace)} pending</span>
              )}
            </span>
          </div>
          <Progress value={fillPct} className="h-1.5" />
        </div>

        {/* Cancel reservation — visible while deal is still open and reservation is not terminal */}
        {!isTerminal && (
          <div className="mt-6 pt-5 border-t border-line flex items-center justify-between flex-wrap gap-3">
            <div className="text-caption text-ink-subtle">
              {canCancel
                ? "Changed your mind? You can cancel anytime before the deal deadline."
                : cancelDisabledReason}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!canCancel}
              onClick={() => setCancelOpen(true)}
              data-testid="cancel-reservation-btn"
              className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="size-3.5 mr-1.5" /> Cancel reservation
            </Button>
          </div>
        )}
      </div>

      {/* Cancel confirmation */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-reservation-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this reservation?</DialogTitle>
            <DialogDescription className="pt-1">
              You&rsquo;re cancelling your {format.currency(reservation.faceValueReserved)} reservation on{" "}
              <span className="font-semibold text-ink">{chosenBrand?.name}</span> for{" "}
              <span className="font-semibold text-ink">{deal?.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-body-sm text-ink-muted">
            {submittedCards.length > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                <AlertTriangle className="size-4 mt-0.5 text-amber-700 shrink-0" />
                <div>
                  You&rsquo;ve already submitted {submittedCards.length} card{submittedCards.length === 1 ? "" : "s"} against this reservation.
                  Approved cards <span className="font-semibold">will still be paid out</span> — only the unused{" "}
                  <span className="font-semibold tabular-nums">{format.currency(Math.max(0, reservation.faceValueReserved - totalApprovedFace))}</span> portion is released.
                </div>
              </div>
            ) : (
              <div className="bg-brand-50 border border-brand-100 rounded-md p-3">
                The full <span className="font-semibold tabular-nums">{format.currency(reservation.faceValueReserved)}</span> will be released back to {chosenBrand?.name}&rsquo;s pool and become available again for you to reserve.
              </div>
            )}
            <p className="text-caption text-ink-subtle">This action is logged in your account audit trail.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-reservation-keep-btn">Keep reservation</Button>
            <Button onClick={handleCancel} data-testid="cancel-reservation-confirm-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              Yes, cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submission interface — the heart */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: paste area */}
        <section className="bg-surface border border-line rounded-xl p-6 flex flex-col">
          {/* For multi-brand deals, show which brand is locked to THIS reservation. */}
          {isMultiBrand && (
            <div data-testid="reservation-brand-lock" className="mb-5 pb-5 border-b border-line">
              <div className="eyebrow mb-2">This reservation</div>
              <div className="inline-flex items-center gap-2.5 bg-brand-50/50 border border-brand-100 rounded-md px-3 py-2">
                {chosenBrand && <BrandLogo brand={chosenBrand} size="xs" />}
                <span className="text-body-sm font-semibold text-ink">{chosenBrand?.name}</span>
                <span className="text-caption text-ink-subtle">— cards must be for this brand</span>
              </div>
              <p className="mt-2 text-caption text-ink-hint leading-relaxed">
                Need to submit cards for another brand on this deal? Make a separate reservation for that brand.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="eyebrow">Step 1</div>
              <h2 className="text-h2 font-brand text-ink mt-1">Paste {isMultiBrand ? `${chosenBrand?.name} ` : ""}card data</h2>
            </div>
            <button
              type="button"
              onClick={() => setRaw(sampleCardLines(chosenBrand?.id, chosenBrand?.name, chosenCfg?.minFace || 100, chosenCfg?.maxFace || 500))}
              data-testid="load-sample-btn"
              className="text-caption font-semibold text-brand-700 hover:text-brand-800 underline-offset-4 hover:underline"
              title="Demo: load 3 fake sample cards in this brand's format"
            >
              Load sample
            </button>
          </div>
          <p className="mt-2 text-caption text-ink-subtle">One card per line. Card number, PIN, face value &mdash; in any order.</p>

          <Textarea
            data-testid="card-paste-textarea"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={"Example format (overwrite when pasting):\n  CARD NUMBER (16 digits)   PIN (4 digits)   $FACE\n  e.g. 6006491234567812   PIN 5582   $200"}
            className="mt-4 flex-1 min-h-[260px] font-mono text-code resize-none bg-surface-alt border-line focus-visible:ring-brand-700 placeholder:text-ink-hint/70"
          />

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-surface-alt border border-line rounded-md p-3">
              <div className="text-caption text-ink-subtle">Cards parsed</div>
              <div className="mt-0.5 font-brand text-h2 text-ink tabular-nums">{parsed.length}</div>
            </div>
            <div className="bg-surface-alt border border-line rounded-md p-3">
              <div className="text-caption text-ink-subtle">Total face</div>
              <div className="mt-0.5 font-brand text-h2 text-ink tabular-nums">{format.currency(totalFace)}</div>
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={validCount === 0 || submitting || (isMultiBrand && !chosenBrandId)}
            data-testid="submit-cards-btn"
            className="mt-5 h-11 bg-brand-700 hover:bg-brand-800 text-white"
          >
            {submitting ? <><Clock className="size-4 mr-2 animate-spin" /> Submitting…</> :
              <><Send className="size-4 mr-2" /> Submit {validCount} {isMultiBrand ? `${chosenBrand?.name} ` : ""}card{validCount === 1 ? "" : "s"}</>}
          </Button>
          <p className="mt-2.5 text-caption text-ink-hint flex items-start gap-1.5">
            <Info className="size-3 mt-0.5 shrink-0" />
            Cards with format issues stay in the queue but won&rsquo;t be submitted. Fix them or remove the line.
          </p>
        </section>

        {/* Right: live parser preview */}
        <section className="bg-surface border border-line rounded-xl overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="eyebrow">Step 2 · Live preview</div>
              <h2 className="text-h2 font-brand text-ink mt-1">Parser preview</h2>
            </div>
            <div className="flex items-center gap-3 text-caption text-ink-subtle">
              <Legend tone="success" label="Valid" />
              <Legend tone="warning" label="Unclear" />
              <Legend tone="danger" label="Invalid" />
            </div>
          </div>

          {parsed.length === 0 ? (
            <div className="flex-1 grid place-items-center px-8 py-10 text-center min-h-[260px]">
              <FileText className="size-8 text-ink-hint mb-3" strokeWidth={1.5} />
              <h3 className="text-body-sm text-ink font-semibold">Awaiting paste</h3>
              <p className="mt-1 text-caption text-ink-muted max-w-xs">
                Each line is parsed in real time. Card number, PIN, and face value are detected by pattern.
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto max-h-[420px]">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-alt sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-4 py-2 text-eyebrow uppercase text-ink-hint w-10">#</th>
                    <th className="px-2 py-2 text-eyebrow uppercase text-ink-hint">Card #</th>
                    <th className="px-3 py-2 text-eyebrow uppercase text-ink-hint">PIN</th>
                    <th className="px-3 py-2 text-eyebrow uppercase text-ink-hint text-right">Face</th>
                    <th className="px-3 py-2 text-eyebrow uppercase text-ink-hint">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/60">
                  {parsed.map((row, i) => (
                    <tr key={i} className="hover:bg-surface-alt/60 transition-colors">
                      <td className="px-4 py-2 text-ink-hint tabular-nums">{i + 1}</td>
                      <td className="px-2 py-2 font-mono text-code text-ink">
                        {row.cardNumber ? format.cardLast4(row.cardNumber) : <span className="text-ink-hint">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-code text-ink">
                        {row.pin ? "•••• " + row.pin.slice(-2) : <span className="text-ink-hint">—</span>}
                      </td>
                      <td className="px-3 py-2 text-ink tabular-nums text-right">
                        {row.faceValue ? format.currency(row.faceValue) : <span className="text-ink-hint">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <RowStatus status={row.status} reason={row.reason} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Already submitted */}
      {submittedCards.length > 0 && (() => {
        const totalFaceAll = submittedCards.reduce((s, c) => s + (c.faceValue || 0), 0);
        const pendingCount = submittedCards.filter(c => c.status === "Pending").length;
        const approvedCount = submittedCards.filter(c => c.status === "Approved").length;
        const rejectedCount = submittedCards.filter(c => c.status === "Rejected" || c.status === "FlaggedFormat" || c.status === "FlaggedAuth").length;
        return (
        <section className="bg-surface border border-line rounded-xl">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-h2 font-brand text-ink">Cards already submitted</h2>
              <p className="text-caption text-ink-subtle mt-0.5">
                {submittedCards.length} card{submittedCards.length === 1 ? "" : "s"} ·{" "}
                {approvedCount > 0 && <span className="text-emerald-700 font-semibold">{approvedCount} approved</span>}
                {approvedCount > 0 && (pendingCount > 0 || rejectedCount > 0) && " · "}
                {pendingCount > 0 && <span className="text-amber-700 font-semibold">{pendingCount} pending review</span>}
                {pendingCount > 0 && rejectedCount > 0 && " · "}
                {rejectedCount > 0 && <span className="text-red-700 font-semibold">{rejectedCount} needs attention</span>}
              </p>
            </div>
            <div className="text-right">
              <div className="text-caption text-ink-hint uppercase tracking-wide">Total face submitted</div>
              <div className="font-brand text-h2 text-ink tabular-nums" data-testid="submitted-cards-total">{format.currency(totalFaceAll)}</div>
            </div>
          </div>
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Submitted</th>
                <th className="px-4 py-3 text-eyebrow uppercase text-ink-hint">Card #</th>
                <th className="px-4 py-3 text-eyebrow uppercase text-ink-hint text-right">Face</th>
                <th className="px-4 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-4 py-3 text-eyebrow uppercase text-ink-hint">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {submittedCards.map((c) => (
                <tr key={c.id} className="hover:bg-surface-alt/60">
                  <td className="px-6 py-3 text-ink-muted">{format.relative(c.submittedAt)}</td>
                  <td className="px-4 py-3 font-mono text-code text-ink">{format.cardLast4(c.cardNumber)}</td>
                  <td className="px-4 py-3 text-ink tabular-nums text-right">{format.currency(c.faceValue)}</td>
                  <td className="px-4 py-3"><StatusBadge entity="card" status={c.status} dot /></td>
                  <td className="px-4 py-3 text-ink-muted text-caption">{c.flagReason || <span className="text-ink-hint">—</span>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-surface-alt border-t border-line">
              <tr>
                <td colSpan={2} className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Total</td>
                <td className="px-4 py-3 text-ink font-bold tabular-nums text-right">{format.currency(totalFaceAll)}</td>
                <td colSpan={2} className="px-4 py-3 text-caption text-ink-subtle">
                  Expected payout @ {chosenCfg?.rate ?? deal?.rate}%: <span className="font-semibold text-brand-700 tabular-nums">{format.currency(Math.round(totalFaceAll * (chosenCfg?.rate ?? deal?.rate ?? 0)) / 100)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </section>
        );
      })()}
    </div>
  );
}

function KV({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-caption text-ink-subtle">{label}</div>
      <div className={`mt-0.5 font-brand text-h2 font-semibold tabular-nums ${accent ? "text-brand-700" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function Legend({ tone, label }) {
  const dot = { success: "bg-emerald-500", warning: "bg-amber-500", danger: "bg-red-500" }[tone];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`size-1.5 rounded-full ${dot}`} />{label}
    </span>
  );
}

function RowStatus({ status, reason }) {
  if (status === "valid") {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-700">
        <CheckCircle2 className="size-3.5" strokeWidth={2.4} /> <span className="text-caption font-medium">Ready</span>
      </span>
    );
  }
  if (status === "warning") {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-700" title={reason}>
        <AlertTriangle className="size-3.5" strokeWidth={2.4} /> <span className="text-caption font-medium">{reason || "Unclear"}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-red-700" title={reason}>
      <AlertTriangle className="size-3.5" strokeWidth={2.4} /> <span className="text-caption font-medium">{reason || "Invalid"}</span>
    </span>
  );
}

// ── Parser ─────────────────────────────────────────────
function parseRaw(text) {
  if (!text || !text.trim()) return [];
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => parseLine(line));
}

function parseLine(line) {
  // Detect 16-digit number (with optional spaces)
  const collapsed = line.replace(/\s+/g, "");
  const cardMatch = collapsed.match(/(\d{16})/);
  // Detect anything that looks like a card number but with letters too
  const hasCardLikeButBad = !cardMatch && /[\dA-Z]{12,}/i.test(collapsed) && /[A-Z]/i.test(collapsed);

  // PIN — look for the 4-digit segment that's not part of the 16-digit
  let pin = null;
  const pinMatch = line.match(/PIN\s*[:#]?\s*(\d{4})/i);
  if (pinMatch) pin = pinMatch[1];
  else {
    // fall back: 4-digit number that isn't inside the card
    const allFour = [...line.matchAll(/\b(\d{4})\b/g)].map((m) => m[1]);
    const cardDigits = cardMatch ? cardMatch[1] : "";
    pin = allFour.find((d) => !cardDigits.includes(d)) || null;
  }

  // Face value — look for $X or X dollars (1-4 digits)
  let faceValue = null;
  const dollarMatch = line.match(/\$\s*(\d{1,4})(?:\.\d{2})?/);
  if (dollarMatch) faceValue = parseInt(dollarMatch[1], 10);

  // Status logic
  if (hasCardLikeButBad) {
    return { cardNumber: collapsed.match(/[\dA-Z]{12,}/i)?.[0] || null, pin, faceValue, status: "invalid", reason: "Non-numeric in card number" };
  }
  if (!cardMatch) {
    return { cardNumber: null, pin, faceValue, status: "invalid", reason: "Card number not 16 digits" };
  }
  if (!pin) {
    return { cardNumber: cardMatch[1], pin: null, faceValue, status: "warning", reason: "PIN not detected" };
  }
  if (!faceValue) {
    return { cardNumber: cardMatch[1], pin, faceValue: null, status: "warning", reason: "Face value not detected" };
  }
  if (pin === "0000") {
    return { cardNumber: cardMatch[1], pin, faceValue, status: "warning", reason: "PIN 0000 — verify" };
  }
  return { cardNumber: cardMatch[1], pin, faceValue, status: "valid" };
}
